import { EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, Layout, Modal, PageHeader, Table } from 'antd';
import { useContext } from 'react';

import { renderBillsColumnStatus } from 'app/components/commons/Bills/Bills';
import { Pagination } from 'app/components/lists/Pagination';
import { Header } from 'app/components/lists/TableHeader';
import { PriceCell } from 'app/components/lists/cells';
import { ModalCheckBox } from 'app/components/pages/Extranet/ExtranetBookings/ModalCheckBox';
import { UserContext } from 'app/context/UserContext/UserContext';
import { useDownloadBill } from 'app/hooks/data/bills/useDownloadBill';
import type { GetBillsQueryParams } from 'app/hooks/data/bills/useGetBills';
import { useGetBills } from 'app/hooks/data/bills/useGetBills';
import { useRelaunchPayout } from 'app/hooks/data/bills/useRelaunchPayout';
import { PSP } from 'app/typings/PSP';
import type { Bill, PayoutStatus } from 'app/typings/bills';
import { PayoutStatusEnum } from 'app/typings/bills';
import { dateAndHour, formatDateWithoutHour } from 'app/utils/dates';
import { useDebounce } from 'app/utils/hooks/useDebounce';
import { isProvider } from 'app/utils/providers';
import { formatPSP } from 'app/utils/psp';
import {
  parseArrayParam,
  useQueryParamHistory,
} from 'app/utils/queryParamHistory';
import { scrollToTop } from 'app/utils/scroll';
import type { SortOrder } from 'app/utils/sort';
import {
  convertFromAntdSortOrder,
  convertToAntdSortOrder,
  formatSortingForAPI,
  getSortOrder,
  isValidSortOrder,
} from 'app/utils/sort';

import './Bills.scss';

export type BillsQueryParam = {
  search: string;
  pageNumber: number;
  dateSorting: SortOrder;
  psp: PSP[];
};

const { Content } = Layout;
const { Column } = Table;

const PAGE_SIZE = 50;

const getProvider = (maybeProvider: string): PSP | undefined => {
  return isProvider(maybeProvider) ? maybeProvider : undefined;
};

const providerDefaultValue: PSP[] = [PSP.Adyen, PSP.Stripe, PSP.StripeIssuing];
const queryParamInit = {
  pageNumber: 1,
  dateSorting: 'descend',
  psp: providerDefaultValue,
} as const;
const queryParamKeys = {
  pageNumber: 'p',
  dateSorting: 'ds',
  psp: 'pr',
  search: 's',
};
const queryParamFormatter = {};
const queryParamExtractor = {
  pageNumber: parseInt,
  dateSorting: getSortOrder,
  psp: parseArrayParam(getProvider),
};

const formatDate = (date: string) => {
  return date && dateAndHour(date);
};

const makePayload = (
  queryParam: Partial<BillsQueryParam>
): GetBillsQueryParams => {
  const validSortOrder = isValidSortOrder(queryParam.dateSorting);

  return {
    search: queryParam.search,
    sortingColumn: validSortOrder ? 'createdAt' : undefined,
    sortingOrder: validSortOrder
      ? formatSortingForAPI(queryParam.dateSorting)
      : undefined,
    psp: queryParam.psp,
    offset: ((queryParam.pageNumber || 1) - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  };
};

export const Bills = () => {
  const { queryParam, updateQueryParam } =
    useQueryParamHistory<BillsQueryParam>(
      queryParamInit,
      queryParamKeys,
      queryParamFormatter,
      queryParamExtractor
    );
  const { isAdmin, isSuperAdmin } = useContext(UserContext);

  const { mutate: downloadBill, isPending: isDownloadingBill } =
    useDownloadBill();

  const { mutateAsync: relaunchPayout } = useRelaunchPayout();

  const debouncedQueryParam = useDebounce(queryParam, 500);

  const { data: bills, isFetching: isLoading } = useGetBills(
    makePayload(debouncedQueryParam)
  );

  const pageNumber = queryParam.pageNumber || 1;

  const handleDownload = async (billId: number) => {
    if (!isDownloadingBill) {
      downloadBill(billId);
    }
  };

  const handleOffset = (x: number) => {
    updateQueryParam({ pageNumber: (queryParam?.pageNumber || 1) + x });

    scrollToTop();
  };

  const dateSortingHandler = (sort: SortOrder) => {
    updateQueryParam({
      dateSorting: sort,
      pageNumber: 1,
    });
  };

  const handleSearch = (search: string) => {
    updateQueryParam({
      search: search,
      pageNumber: 1,
    });
  };

  const handleProviderQuery = (providers: PSP[]) => {
    updateQueryParam({
      psp: providers,
      pageNumber: 1,
    });
  };

  const handleProviderFilter = (providers: PSP[]) => {
    handleProviderQuery(!!providers.length ? providers : providerDefaultValue);
  };

  const handleRowActions = ({ id }: Bill) => ({
    onClick: () => handleDownload(id),
  });

  const handleReLaunchPayout = async (billId: number, payoutId?: number) => {
    relaunchPayout({ billId, payoutId });
  };

  const actionMenu = (bill: Bill) => {
    const isErrorStatus = bill.status === PayoutStatusEnum.ERROR;
    const isBlockedStatus = bill.status === PayoutStatusEnum.BLOCKED;
    const isAdyen = bill.provider === PSP.Adyen;
    const isAdyenToSend = isAdyen && bill.status === PayoutStatusEnum.TO_SEND;

    const canShowRelaunch =
      !isAdyenToSend &&
      ((isAdmin && isErrorStatus) || (isSuperAdmin && isAdyen));

    const onClickRelaunch = () => {
      if (isSuperAdmin && isAdyen && !(isErrorStatus || isBlockedStatus)) {
        Modal.confirm({
          title: 'Are you sure you want to issue again this payment ?',
          content:
            'Proceed only if you are sure that the payment failed in Adyen',
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => handleReLaunchPayout(bill.id, bill.payoutId),
        });

        return;
      }

      handleReLaunchPayout(bill.id, bill.payoutId);
    };

    return (
      <div className="actions-menu">
        <Dropdown
          menu={{
            items: [
              {
                key: 'download',
                label: 'Download',
                disabled: isDownloadingBill,
                onClick: () => handleDownload(bill.id),
              },
              ...(canShowRelaunch
                ? [
                    {
                      key: 'relaunch',
                      label: 'Retry payout',
                      onClick: () => onClickRelaunch(),
                    },
                  ]
                : []),
            ],
          }}
          trigger={['click']}
        >
          <EllipsisOutlined rotate={90} />
        </Dropdown>
      </div>
    );
  };

  return (
    <Layout className="bills-list">
      <PageHeader className="header" title="Bills" />
      <Content className="body">
        <Table
          dataSource={bills ?? []}
          pagination={false}
          title={() => (
            <Header
              handleSearch={handleSearch}
              searchValue={queryParam.search || ''}
              pageNumber={pageNumber}
              pageResultsLength={bills?.length ?? 0}
              handleOffset={handleOffset}
            />
          )}
          loading={isLoading}
          rowKey="id"
          onRow={handleRowActions}
          onChange={(__, ___, sorter) => {
            // @ts-ignore -- legacy directive, needs investigation
            const { order } = sorter;

            dateSortingHandler(convertFromAntdSortOrder(order));
          }}
        >
          <Column title="Hotel" dataIndex="hotelName" />
          <Column title="Invoice name #" dataIndex="reference" />
          <Column
            title="Total net amount"
            dataIndex="totalAmount"
            render={(price, entity) => (
              <PriceCell price={price} entity={entity} defaultText="Missing" />
            )}
          />
          <Column
            title="Total comission"
            dataIndex="commissionAmount"
            render={(price, entity) => (
              <PriceCell price={price} entity={entity} defaultText="Missing" />
            )}
          />
          <Column
            title="Creation date"
            dataIndex="createdAt"
            sorter
            sortOrder={convertToAntdSortOrder(queryParam.dateSorting)}
            render={formatDate}
          />

          <Column
            title="Period"
            render={({ fromDate, toDate }) => {
              if (fromDate && toDate) {
                const from = formatDateWithoutHour(new Date(fromDate));
                const to = formatDateWithoutHour(new Date(toDate));

                return `${from} - ${to}`;
              }

              return null;
            }}
          />
          <Column
            title={
              <ModalCheckBox
                title="Provider"
                defaultValue={providerDefaultValue}
                onChange={handleProviderFilter}
                items={[
                  {
                    value: PSP.Adyen,
                    label: 'Adyen Payout',
                  },
                  {
                    value: PSP.Stripe,
                    label: 'Stripe Payout',
                  },
                  {
                    value: PSP.StripeIssuing,
                    label: 'Stripe VCC',
                  },
                ]}
                value={queryParam.psp}
              />
            }
            dataIndex="provider"
            render={formatPSP}
          />
          <Column
            title="Payout Status"
            dataIndex="status"
            render={(status: PayoutStatus, bill: Bill) =>
              renderBillsColumnStatus(status, bill, false)
            }
          />
          <Column
            render={actionMenu}
            onCell={() => ({ onClick: (e) => e.stopPropagation() })}
            align="center"
          />
        </Table>
        <Pagination
          className="footer"
          pageNumber={pageNumber}
          pageResultsLength={bills?.length ?? 0}
          handleOffset={handleOffset}
        />
      </Content>
    </Layout>
  );
};
